import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import {
  Sprint,
  SprintBoard,
  SprintColumn,
  SprintDashboardCard,
  SprintParticipant,
  SprintRequest
} from '../models/sprint.model';
import { GdpTask, TaskRequest } from '../models/task.model';
import { UserStory } from '../models/user-story.model';

@Injectable({ providedIn: 'root' })
export class GdpSprintService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.baseApiUrl}/gdp`;

  // ── Sprints ────────────────────────────────────────────────────────────────

  getSprints(projectId: number): Observable<Sprint[]> {
    return this.http.get<Sprint[]>(`${this.apiUrl}/projects/${projectId}/sprints`);
  }

  createSprint(projectId: number, request: SprintRequest): Observable<Sprint> {
    return this.http.post<Sprint>(`${this.apiUrl}/projects/${projectId}/sprints`, request);
  }

  getSprint(sprintId: number): Observable<Sprint> {
    return this.http.get<Sprint>(`${this.apiUrl}/sprints/${sprintId}`);
  }

  updateSprint(sprintId: number, request: SprintRequest): Observable<Sprint> {
    return this.http.put<Sprint>(`${this.apiUrl}/sprints/${sprintId}`, request);
  }

  deleteSprint(sprintId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/sprints/${sprintId}`);
  }

  launchSprint(sprintId: number): Observable<Sprint> {
    return this.http.post<Sprint>(`${this.apiUrl}/sprints/${sprintId}/launch`, {});
  }

  closeSprint(sprintId: number): Observable<Sprint> {
    return this.http.post<Sprint>(`${this.apiUrl}/sprints/${sprintId}/close`, {});
  }

  // ── Tareas del sprint ──────────────────────────────────────────────────────

  assignTask(sprintId: number, taskId: number): Observable<GdpTask> {
    return this.http.post<GdpTask>(`${this.apiUrl}/sprints/${sprintId}/tasks`, { taskId });
  }

  removeTask(sprintId: number, taskId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/sprints/${sprintId}/tasks/${taskId}`);
  }

  /** Crea una tarea "solo de sprint" (vive en un backlog auxiliar oculto). */
  createSprintTask(sprintId: number, request: TaskRequest): Observable<GdpTask> {
    return this.http.post<GdpTask>(`${this.apiUrl}/sprints/${sprintId}/tasks`, request);
  }

  // ── User stories del sprint ────────────────────────────────────────────────

  /** Asigna la US ENTERA (con sus tareas hijas) al sprint. */
  assignUserStory(sprintId: number, userStoryId: number): Observable<UserStory> {
    return this.http.post<UserStory>(`${this.apiUrl}/sprints/${sprintId}/user-stories`, { userStoryId });
  }

  /** Quita la US ENTERA (con sus tareas hijas) del sprint. */
  removeUserStory(sprintId: number, userStoryId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/sprints/${sprintId}/user-stories/${userStoryId}`);
  }

  // ── Participantes ──────────────────────────────────────────────────────────

  getParticipants(sprintId: number): Observable<SprintParticipant[]> {
    return this.http.get<SprintParticipant[]>(`${this.apiUrl}/sprints/${sprintId}/participants`);
  }

  addParticipant(sprintId: number, accountId: number, spBudget: number | null): Observable<SprintParticipant> {
    return this.http.post<SprintParticipant>(`${this.apiUrl}/sprints/${sprintId}/participants`, { accountId, spBudget });
  }

  updateParticipant(sprintId: number, participantId: number, spBudget: number | null): Observable<SprintParticipant> {
    return this.http.put<SprintParticipant>(
      `${this.apiUrl}/sprints/${sprintId}/participants/${participantId}`, { spBudget }
    );
  }

  removeParticipant(sprintId: number, participantId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/sprints/${sprintId}/participants/${participantId}`);
  }

  // ── Columnas ───────────────────────────────────────────────────────────────

  /** USs del sprint (para desgloses fuera del tablero) */
  getSprintUserStories(sprintId: number): Observable<UserStory[]> {
    return this.http.get<UserStory[]>(`${this.apiUrl}/sprints/${sprintId}/user-stories`);
  }

  /** Crea una US nueva directamente en el sprint (backlog auxiliar) */
  createSprintUserStory(sprintId: number, name: string): Observable<UserStory> {
    return this.http.post<UserStory>(`${this.apiUrl}/sprints/${sprintId}/user-stories`, { name });
  }

  /** TODAS las tareas del sprint (incluidas las del backlog auxiliar y las de US) */
  getSprintTasks(sprintId: number): Observable<GdpTask[]> {
    return this.http.get<GdpTask[]>(`${this.apiUrl}/sprints/${sprintId}/tasks`);
  }

  /** Reordena las bandas de US del tablero (arrastrar la card amarilla) */
  reorderBoardUserStories(sprintId: number, userStoryIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/sprints/${sprintId}/user-stories/reorder`, { userStoryIds });
  }

  /**
   * Reordena la cesta del sprint (solo en planificación): recibe TODAS las
   * tareas en orden visual — hijas de cada US y después las sueltas. Ese
   * orden será el de la primera columna al lanzar el sprint.
   */
  reorderBasketTasks(sprintId: number, taskIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/sprints/${sprintId}/tasks/reorder`, { taskIds });
  }

  getColumns(sprintId: number): Observable<SprintColumn[]> {
    return this.http.get<SprintColumn[]>(`${this.apiUrl}/sprints/${sprintId}/columns`);
  }

  addColumn(sprintId: number, name: string, isDoneColumn: boolean, isWaitColumn = false): Observable<SprintColumn> {
    return this.http.post<SprintColumn>(`${this.apiUrl}/sprints/${sprintId}/columns`, { name, isDoneColumn, isWaitColumn });
  }

  updateColumn(sprintId: number, columnId: number, name: string, isDoneColumn: boolean, isWaitColumn = false): Observable<SprintColumn> {
    return this.http.put<SprintColumn>(`${this.apiUrl}/sprints/${sprintId}/columns/${columnId}`, { name, isDoneColumn, isWaitColumn });
  }

  deleteColumn(sprintId: number, columnId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/sprints/${sprintId}/columns/${columnId}`);
  }

  reorderColumns(sprintId: number, columnIds: number[]): Observable<SprintColumn[]> {
    return this.http.put<SprintColumn[]>(`${this.apiUrl}/sprints/${sprintId}/columns/order`, { columnIds });
  }

  // ── Tablero ────────────────────────────────────────────────────────────────

  getBoard(sprintId: number): Observable<SprintBoard> {
    return this.http.get<SprintBoard>(`${this.apiUrl}/sprints/${sprintId}/board`);
  }

  moveTask(
    taskId: number,
    columnId: number,
    position: number,
    zone?: { afterUserStoryId: number | null }
  ): Observable<GdpTask> {
    if (zone) {
      return this.http.put<GdpTask>(`${this.apiUrl}/tasks/${taskId}/move`, {
        columnId, position, afterUserStoryId: zone.afterUserStoryId, moveToZone: true
      });
    }
    return this.http.put<GdpTask>(`${this.apiUrl}/tasks/${taskId}/move`, { columnId, position });
  }

  // ── Dashboard ──────────────────────────────────────────────────────────────

  getDashboardCards(): Observable<SprintDashboardCard[]> {
    return this.http.get<SprintDashboardCard[]>(`${this.apiUrl}/dashboard/sprints`);
  }
}
